
<template>

  <div class="wrapper">
    <div id="anchor-step-1" class="box-container bg-grey">
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-6">
            <figure class="image_container">
              <picture>
                <source srcset="files/calculator/schritt_01.webp" type="image/webp">
                <img src="files/calculator/schritt_01.jpg" width="800" height="700" alt="" loading="lazy" itemprop="image">
              </picture>
            </figure>
          </div>
          <div class="col-12 col-lg-6 box">
            <div>
              <span>Step 1</span>
              <h3>Hourly rate / <br>maintenance contracts</h3>
              <div>
                <p>For the calculation we need your hourly rate.</p>
                <div class="row">
                  <input class="col-7 col-sm-8" id="rs-range-line" type="range" min="3000" max="18000" step="1000" v-model="hourlyRate">
                  <label class="col-5 col-sm-4 font-color" for="rs-range-line">{{ formatPrice(hourlyRate) }}</label>
                </div>
              </div>
              <div>
                <p class="margin-top">Do you already have maintenance contracts?</p>
                <input id="toggle-yes" class="toggle" type="radio" name="contracts" @change="hasContracts = true" checked><label for="toggle-yes">Yes</label><input id="toggle-no" class="toggle" type="radio" name="contracts" @change="hasContracts = false"><label for="toggle-no">No</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="box-container right">
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-6 order-lg-last">
            <figure class="image_container">
              <picture>
                <source srcset="files/calculator/schritt_02.webp" type="image/webp">
                <img src="files/calculator/schritt_02.jpg" width="800" height="700" alt="" loading="lazy" itemprop="image">
              </picture>
            </figure>
          </div>
          <div class="col-12 col-lg-6 order-lg-first box">
            <span>Step 2</span>
            <h3>Monthly bill / <br>installations</h3>
            <template v-if="hasContracts">
              <p>How much do you charge (on average) per month?</p>
              <div>
                <div class="row">
                  <input class="col-7 col-sm-8" id="calculateContractPrice" type="range" min="500" max="8000" step="500" v-model="calculateContractPrice">
                  <label class="col-5 col-sm-4 font-color" for="calculateContractPrice">{{ formatPrice(calculateContractPrice) }}</label>
                </div>
              </div>
              <p class="margin-top">How many installations with maintenance contracts do you maintain?</p>
              <div class="quantity">
                <input id="paidInstallations" type="number" v-model="paidInstallations"/>
                <label for="paidInstallations"></label>
              </div>
            </template>
            <template v-else>
              <p>How long does it take you to update Contao?</p>
              <div class="quantity">
                <input id="calculateMinutes" type="number" v-model="calculateMinutes"/>
                <label for="calculateMinutes">min</label>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>



    <div class="box-container bg-grey result">
      <div class="container">
        <div class="row">
          <div class="col-12 offset-lg-2 col-lg-8">
            <div>
              <h2>Calculation result</h2>
              <template v-if="hasContracts">
                <p>Based on your monthly price, we estimate that you have calculated with <strong>approx. {{ recommendedMinutes }} minutes of effort per update*</strong>.</p>
              </template>
              <template v-else>
                <p>
                  Based on your hourly rate and the estimated duration of work*,
                  you need to charge <strong>at least {{ formatPrice(recommendedPricePerMonth) }}
                  per month</strong>. With this you earn the same amount as if you would have worked on paid jobs during this time.
                </p>
                <p>
                  With <strong>maintenance contracts for</strong> <input type="number" v-model="paidInstallations">
                  <strong>installations</strong> you will achieve guaranteed <strong>revenues of {{ formatPrice(contractRevenue) }} per year</strong>.
                </p>
              </template>
              <template v-if="profitWithTrakked > 0">
                <p>
                  With trakked you can reduce your <strong>update effort</strong> estimated <strong>by two third,
                  that is about {{ downtimeWithTrakked }} hours per year</strong>.
                  If you do <strong>compensable work</strong> during this time, you will earn <strong>up to
                  {{ formatPrice(profitWithTrakked) }} extra</strong> per year (trakked subscription included).
                </p>
                <p>
                  In other words: even if you charge your <strong>maintenance contracts for {{ formatPrice(trakkedPricePerMonth, 'ceil') }} per month</strong>,
                  you would still be <strong>profitable</strong> with trakked!
                </p>
              </template>
              <p v-else>
                Unfortunately, trakked.io is not currently worth it for you.
              </p>
              <p><small>* We assume that there are on average {{ updatesPerYear }} Contao updates per year.</small></p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="anchor-kalkulator" class="box-container">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="details">
              <h2>Calculator for maintenance contracts</h2>

              <div class="row">
                <div class="col-12 col-lg-6">
                  <h3>Details</h3>
                  <div class="row align-items-center">
                    <div class="col-6">Hourly rate</div>
                    <div class="col-3"><input type="range" min="3000" max="18000" step="1000" v-model="hourlyRate"></div>
                    <div class="col-3 font-color">{{ formatPrice(hourlyRate) }}</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6">Monthly fee per maintenance contract</div>
                    <div class="col-3"><input type="range" min="500" max="8000" step="500" v-model="contractPricePerMonth"></div>
                    <div class="col-3 font-color">{{ formatPrice(contractPricePerMonth) }}</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6">Installations with contract</div>
                    <div class="col-6"><input type="number" v-model="paidInstallations"></div>
                  </div>
                </div>
                <div class="col-12 col-lg-6">
                  <h3>Assumptions</h3>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Contao updates per year</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="updatesPerYear"></div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Effort without trakked</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="minutesWithoutTrakked"> min</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Effort with trakked</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="minutesWithTrakked"> min</div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">trakked installations without contract</div>
                    <div class="col-6 col-md-4"><input type="number" v-model="unpaidInstallations"></div>
                  </div>
                  <div class="row align-items-center">
                    <div class="col-6 col-md-8">Updates for unpaid installations</div>
                    <div class="col-6 col-md-4"><input id="unpaid-no" class="toggle" name="unpaid" type="radio" @change="unpaidUpdates = false" checked><label for="unpaid-no">No</label><input id="unpaid-yes" class="toggle" name="unpaid" type="radio" @change="unpaidUpdates = true"><label for="unpaid-yes">Yes</label></div>
                  </div>

                </div>
              </div>


              <h3>Calculation for 12 months</h3>
              <div class="table-scrollable">
                <table class="sum">
                  <thead>
                  <tr>
                    <th></th>
                    <th>WITHOUT trakked</th>
                    <th>WITH trakked</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td>Guaranteed revenue</td>
                    <td style="text-align:right">{{ formatPrice(contractRevenue) }}</td>
                    <td style="text-align:right">{{ formatPrice(contractRevenue) }}</td>
                  </tr>
                  <tr>
                    <td>Effort for updates</td>
                    <td style="text-align:right"><abbr :title="`${minutesWithoutTrakked}min x ${updatedInstallations} Installationen x ${updatesPerYear} Updates pro Jahr`">{{ Math.ceil(effortWithoutTrakked / 60) }}h</abbr> x {{ formatPrice(hourlyRate) }} = {{ formatPrice(costWithoutTrakked) }}</td>
                    <td style="text-align:right"><abbr :title="`${minutesWithTrakked}min x ${updatedInstallations} Installationen x ${updatesPerYear} Updates pro Jahr`">{{ Math.ceil(effortWithTrakked / 60) }}h</abbr> x {{ formatPrice(hourlyRate) }} = {{ formatPrice(costWithTrakked) }}</td>
                  </tr>
                  <tr>
                    <td>Expenditure for trakked</td>
                    <td style="text-align:right">–</td>
                    <td style="text-align:right">{{ formatPrice(priceForTrakked) }}</td>
                  </tr>
                  </tbody>
                  <tfoot>
                  <tr>
                    <td>Possible additional income/costs</td>
                    <td style="text-align:right">{{ formatPrice(profitWithoutTrakked) }}</td>
                    <td style="text-align:right">{{ formatPrice(profitWithTrakked) }}</td>
                  </tr>
                  <tr>
                    <td><i>Alternative:</i> Free time thanks to contracts</td>
                    <td style="text-align:right">
                      <template v-if="downtimeWithoutTrakked !== 0">approx. {{ downtimeWithoutTrakked }} Hours</template>
                      <template v-else><i>none</i></template>
                    </td>
                    <td style="text-align:right">
                      <template v-if="downtimeWithTrakked !== 0">approx. {{ downtimeWithTrakked }} Hours</template>
                      <template v-else><i>none</i></template>
                    </td>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

</template>

<script>
export default {

  data() {
    return {
      hasContracts: true,
      calculateContractPrice: 2000,
      calculateMinutes: 15,

      updatesPerYear: 12,
      minutesWithTrakked: 5,
      minutesWithoutTrakked: 15,
      contractPricePerMonth: 2000,
      pricePerInstallation: 249,
      hourlyRate: 8000,
      minimumInstallations: 10,
      paidInstallations: 10,
      unpaidInstallations: 0,
      unpaidUpdates: false,
    }
  },

  computed: {
    updatedInstallations: (vm) => Number(vm.paidInstallations) + (vm.unpaidUpdates ? Number(vm.unpaidInstallations) : 0),
    billedInstallations: (vm) => Math.max(vm.minimumInstallations, (Number(vm.paidInstallations) + Number(vm.unpaidInstallations))),
    contractRevenue: (vm) => vm.contractPricePerMonth * 12 * vm.paidInstallations,
    priceForTrakked: (vm) => 12 * vm.billedInstallations * vm.pricePerInstallation,

    effortWithTrakked: (vm) => vm.minutesWithTrakked * vm.updatedInstallations * vm.updatesPerYear,
    effortWithoutTrakked: (vm) => vm.minutesWithoutTrakked * vm.updatedInstallations * vm.updatesPerYear,

    costWithTrakked: (vm) => vm.hourlyRate / 60 * vm.effortWithTrakked,
    costWithoutTrakked: (vm) => vm.hourlyRate / 60 * vm.effortWithoutTrakked,

    profitWithTrakked: (vm) => vm.contractRevenue - vm.costWithTrakked - vm.priceForTrakked,
    profitWithoutTrakked: (vm) => vm.contractRevenue - vm.costWithoutTrakked,

    hourlyRateWithoutTrakked: (vm) => vm.contractRevenue / (vm.effortWithoutTrakked / 60),
    hourlyRateWithTrakked: (vm) => (vm.contractRevenue - vm.priceForTrakked) / (vm.effortWithTrakked / 60),

    downtimeWithTrakked: (vm) => Math.round(vm.profitWithTrakked / vm.hourlyRate),
    downtimeWithoutTrakked: (vm) => Math.round(vm.profitWithoutTrakked / vm.hourlyRate),

    recommendedPricePerMonth: (vm) => vm.hourlyRate / (60 / vm.minutesWithoutTrakked) * vm.updatesPerYear / 12,
    recommendedMinutes: (vm) => Math.ceil(60 / (vm.hourlyRate / (vm.calculateContractPrice * 12 / vm.updatesPerYear))),
    trakkedPricePerMonth: (vm) => vm.hourlyRate / (60 / vm.minutesWithTrakked) * vm.updatesPerYear / 12,
  },

  methods: {
    formatPrice(value, method = false) {
      value = method ? Math[method](value / 100) : (value / 100)

      if (Intl.NumberFormat) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: 'EUR',
          currencyDisplay: 'symbol',
        }).format(value);
      }

      return `${value.toFixed(2)} €`;
    }
  },

  watch: {
    calculateContractPrice() {
      this.contractPricePerMonth = this.calculateContractPrice;

      this.minutesWithoutTrakked = this.recommendedMinutes;
      this.minutesWithTrakked = Math.max(Math.ceil(this.minutesWithoutTrakked / 3), this.minutesWithoutTrakked - 30);
    },

    calculateMinutes() {
      this.minutesWithoutTrakked = this.calculateMinutes;
      this.minutesWithTrakked = Math.max((this.minutesWithoutTrakked / 3), this.minutesWithoutTrakked - 30);

      this.contractPricePerMonth = this.recommendedPricePerMonth;
    },
  },
}
</script>
